import React, { useRef, useState, useEffect } from 'react';
import axiosInstance from '../../Axios';
import Toast from '../../services/Toast';
import { useBrand } from '../../hooks/useBrand';
import ProductsTagline from '../products-tagline/ProductsTagline';
import './ResetPasswordOtpForm.css';

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;
const MAX_RESEND_ATTEMPTS = 3;

interface ResetPasswordOtpFormProps {
  userData: { id: number; email: string };
  onSuccess: (otp: string) => void;
  onBack: () => void;
}

const ResetPasswordOtpForm: React.FC<ResetPasswordOtpFormProps> = ({ userData, onSuccess, onBack }) => {
  const [otp, setOtp] = useState<string[]>(new Array(OTP_LENGTH).fill(''));
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const [resendAttempts, setResendAttempts] = useState(0);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const { brand, isBranded, style: brandStyle } = useBrand();

  // Cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleChange = (index: number, value: string) => {
    let otpValue = [...otp];
    if (value.toString().length > 1) {
      if (otpValue[index]) {
        otpValue[index] = value.toString()[1];
        inputRefs.current[index + 1]?.focus();
        setOtp(otpValue);
        return;
      }
      for (let i = 0; i < value.toString().length; i++) {
        if (index === 5) {
          inputRefs.current[index]?.focus();
          break;
        }
        otpValue[i] = value.toString()[i];
        inputRefs.current[i + 1]?.focus();
        setOtp(otpValue);
      }
    } else {
      if (!/^\d?$/.test(value)) return;
      otpValue[index] = value.toString()[0];
      setOtp(otpValue);
      if (value && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = async () => {
    if (otp.includes('')) {
      Toast.error('Please enter the complete code');
      return;
    }

    try {
      setLoading(true);
      const payload = { otp: otp.join(''), id: userData.id };
      const response = await axiosInstance.post('/auth/verify-forgot-password-otp', payload);

      if (!response?.data?.status) {
        Toast.error('Something went wrong. Please try again later.');
        setLoading(false);
        return;
      }

      Toast.success('OTP verified successfully');
      onSuccess(otp.join(''));
    } catch (error) {
      setLoading(false);
      setOtp(new Array(OTP_LENGTH).fill(''));
    }
  };

  const resendOtp = async () => {
    if (resendCooldown > 0 || resendAttempts >= MAX_RESEND_ATTEMPTS) {
      return;
    }

    try {
      setResendLoading(true);
      const payload = { id: userData.id };
      const response = await axiosInstance.post('/auth/resend-forgot-password-otp', payload);

      if (!response?.data?.status) {
        Toast.error('Something went wrong. Please try again later.');
        setResendLoading(false);
        return;
      }

      Toast.success('OTP sent to your registered email address.');
      setResendAttempts(prev => prev + 1);
      setResendCooldown(RESEND_COOLDOWN_SECONDS);
      setResendLoading(false);
    } catch (error) {
      setResendLoading(false);
    }
  };

  const remainingAttempts = MAX_RESEND_ATTEMPTS - resendAttempts;
  const isResendDisabled = resendLoading || resendCooldown > 0 || resendAttempts >= MAX_RESEND_ATTEMPTS;

  const getResendButtonText = () => {
    if (resendLoading) {
      return (
        <span className="reset-otp-resend-btn__loading">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
            <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
          </svg>
          Sending...
        </span>
      );
    }

    if (resendAttempts >= MAX_RESEND_ATTEMPTS) {
      return (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <line x1="15" y1="9" x2="9" y2="15"/>
            <line x1="9" y1="9" x2="15" y2="15"/>
          </svg>
          No attempts left
        </>
      );
    }

    if (resendCooldown > 0) {
      return (
        <>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/>
            <polyline points="12 6 12 12 16 14"/>
          </svg>
          Resend in {resendCooldown}s
        </>
      );
    }

    return (
      <>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
        </svg>
        Resend Code
      </>
    );
  };

  return (
    <div className="reset-otp-card" style={brandStyle} data-brand={brand.key || 'nextek'}>
      <div className="reset-otp-card__inner">
        {/* Decorative elements */}
        <div className="reset-otp-card__decoration">
          <div className="reset-otp-card__circle reset-otp-card__circle--1" />
          <div className="reset-otp-card__circle reset-otp-card__circle--2" />
          <div className="reset-otp-card__dots">
            {[...Array(6)].map((_, i) => (
              <span key={i} style={{ animationDelay: `${i * 0.1}s` }} />
            ))}
          </div>
        </div>

        {/* Icon */}
        <div className="reset-otp-card__icon">
          <div className="reset-otp-card__icon-bg">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              <circle cx="12" cy="16" r="1"/>
            </svg>
          </div>
          <div className="reset-otp-card__key">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
              <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
            </svg>
          </div>
        </div>

        {/* Header */}
        <div className="reset-otp-card__header">
          <h1 className="reset-otp-card__title">
            <span className="reset-otp-card__title-accent">Enter</span> OTP
          </h1>
          <p className="reset-otp-card__subtitle">
            We've sent a 6-digit code to your email
          </p>
          <div className="reset-otp-card__email">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <rect x="2" y="4" width="20" height="16" rx="2"/>
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
            </svg>
            <span>{userData.email}</span>
          </div>
        </div>

        {/* OTP Form */}
        <form className="reset-otp-form" onSubmit={(e) => e.preventDefault()} data-cy="reset-otp-form">
          <div className="reset-otp-group">
            {otp.map((digit, index) => (
              <div key={index} className={`reset-otp-input ${digit ? 'reset-otp-input--filled' : ''}`}>
                <input
                  ref={(el) => (inputRefs.current[index] = el)}
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={digit || ''}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  autoComplete="off"
                  placeholder="·"
                  data-cy={`reset-otp-input-${index}`}
                />
                <div className="reset-otp-input__highlight" />
              </div>
            ))}
          </div>

          <button
            type="button"
            disabled={loading}
            onClick={handleVerify}
            className={`reset-otp-btn ${loading ? 'reset-otp-btn--loading' : ''}`}
            data-cy="reset-otp-verify-btn"
          >
            {loading ? (
              <div className="reset-otp-btn__spinner">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                  <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                </svg>
              </div>
            ) : (
              <>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                  <polyline points="22 4 12 14.01 9 11.01"/>
                </svg>
                Verify Code
              </>
            )}
          </button>
        </form>

        {/* Resend */}
        <div className="reset-otp-resend">
          <p>Didn't receive the code?</p>
          <button
            type="button"
            className={`reset-otp-resend-btn ${isResendDisabled ? 'reset-otp-resend-btn--disabled' : ''}`}
            onClick={resendOtp}
            disabled={isResendDisabled}
            data-cy="reset-otp-resend-btn"
          >
            {getResendButtonText()}
          </button>
          {resendAttempts > 0 && resendAttempts < MAX_RESEND_ATTEMPTS && (
            <p className="reset-otp-resend-attempts">
              {remainingAttempts} {remainingAttempts === 1 ? 'attempt' : 'attempts'} remaining
            </p>
          )}
        </div>

        {/* Back Button */}
        <div className="reset-otp-back">
          <button type="button" onClick={onBack} className="reset-otp-back-btn" data-cy="reset-otp-back-btn">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="m12 19-7-7 7-7"/>
              <path d="M19 12H5"/>
            </svg>
            Go Back
          </button>
        </div>

        {/* Footer */}
        <div className="reset-otp-footer">
          <ProductsTagline signingInTo={isBranded ? brand.name : null} />
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordOtpForm;
